// syncDiff.mjs
//
// PURE, Node-testable helpers for the load-time server sync push. Imported by
// App.jsx's serverSaveAll; tested by `node --test src/syncDiff.test.mjs`.
//
// The bug they fix: serverSaveAll used to re-push the WHOLE library on every
// Studio load as ~52 concurrent whole-document writes (1 save-profiles +
// 1 save-groups + one save-template PER template, 60ms apart). Each write did a
// load-modify-write of the single multi-MB per-user blob. 50 of them against one
// key starved reads (a member opening the Studio mid-storm got a failed load →
// blank grid) and, being UNSERIALIZED read-modify-write, could silently drop
// updates — strong consistency guarantees read-after-committed-write, it does NOT
// serialize overlapping RMW cycles, so a stale copy can clobber a concurrent one.
//
// The rule here: on load, compute what actually DIFFERS from the server snapshot
// we just read and push ONLY that — a clean load (nothing changed) writes NOTHING.
// Templates that do differ are size-chunked so the client can send them in a few
// SEQUENTIAL batch writes instead of N concurrent ones.

// Deterministic stringify: object keys sorted, undefined dropped in objects and
// coerced to null in arrays (matching JSON.stringify), so two structurally-equal
// values always compare equal regardless of key insertion order (server items
// round-trip through JSON.parse; local items come from IndexedDB — key order can
// differ without the content differing).
export function stableStringify(v) {
  if (v === null || typeof v !== "object") {
    const s = JSON.stringify(v);
    return s === undefined ? "null" : s; // undefined / function → null (JSON semantics)
  }
  if (Array.isArray(v)) {
    return "[" + v.map((x) => stableStringify(x)).join(",") + "]";
  }
  const keys = Object.keys(v).filter((k) => v[k] !== undefined).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(v[k])).join(",") + "}";
}

// Order-sensitive deep equality (used for the profiles/groups arrays, where a
// pure reorder IS a change the member expects to persist).
export function sameDeep(a, b) {
  return stableStringify(a) === stableStringify(b);
}

// Templates that must be pushed: present in local but MISSING or DIFFERENT on the
// server (keyed by id, compared by content). Order among templates is not
// authoritative (each template carries its own sortOrder), so this is id-based —
// a sortOrder change lives inside the template and so shows up as a content diff.
export function diffTemplates(local, server) {
  const L = Array.isArray(local) ? local : [];
  const smap = new Map(
    (Array.isArray(server) ? server : [])
      .filter((t) => t && t.id != null)
      .map((t) => [String(t.id), t])
  );
  return L.filter((t) => {
    if (!t || t.id == null) return false;
    const s = smap.get(String(t.id));
    return !s || stableStringify(t) !== stableStringify(s);
  });
}

// Greedy size-bounded chunking. Each chunk stays under maxBytes so a batch POST
// never exceeds the server's single-payload cap; an item larger than maxBytes
// goes alone (the server then rejects it honestly — same as the old per-template
// path). sizeOf(item) returns the item's serialized byte length.
export function chunkBySize(items, maxBytes, sizeOf) {
  const out = [];
  let cur = [];
  let curBytes = 0;
  for (const it of Array.isArray(items) ? items : []) {
    const b = sizeOf(it);
    if (cur.length && curBytes + b > maxBytes) {
      out.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(it);
    curBytes += b;
  }
  if (cur.length) out.push(cur);
  return out;
}
